import { Fragment } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  ButtonBase,
  CompactTextField,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  Typography
} from '@/shared/ui';
import { DisplaySettingsIcon, ExpandMore } from '@/shared/ui/icons';
import Popover from '@/shared/ui/overlays/Popover';

import { Z_INDEX } from '@/config/z-index';
import { FeatureFlag } from '@/services/feature-flags';
import { withFeatureFlag } from '@/services/feature-flags/react';

import { KNOBS, type FuzzySearchSettings, type KnobDef } from '../settingsSchema';
import { useFuzzySearchSettings } from '../useFuzzySearchSettings';

type SetOption = ReturnType<typeof useFuzzySearchSettings>['setOption'];

const renderControl = (knob: KnobDef, settings: FuzzySearchSettings, setOption: SetOption) => {
  const value = settings[knob.key];

  if (knob.control.kind === 'switch') {
    return (
      <Switch
        checked={value as boolean}
        slotProps={{ input: { 'aria-label': knob.label } }}
        onChange={(_, checked) => setOption(knob.key, checked)}
      />
    );
  }

  if (knob.control.kind === 'number') {
    return (
      <CompactTextField
        label={knob.label}
        slotProps={{
          htmlInput: {
            min: knob.control.min,
            max: knob.control.max,
            step: knob.control.step
          }
        }}
        sx={{ width: 110 }}
        type='number'
        value={value as number}
        onChange={event => setOption(knob.key, Number(event.target.value))}
      />
    );
  }

  return (
    <Select
      aria-label={knob.label}
      size='small'
      value={value as string}
      onChange={event =>
        setOption(knob.key, event.target.value as FuzzySearchSettings['tokenMatch'])
      }
    >
      {knob.control.options.map(option => (
        <MenuItem
          key={option}
          value={option}
        >
          {option}
        </MenuItem>
      ))}
    </Select>
  );
};

const renderKnob = (knob: KnobDef, settings: FuzzySearchSettings, setOption: SetOption) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.75 }}>
    {knob.control.kind === 'switch' ? (
      <FormControlLabel
        control={renderControl(knob, settings, setOption)}
        label={knob.label}
        sx={{ m: 0 }}
      />
    ) : (
      renderControl(knob, settings, setOption)
    )}
    <Typography
      color='info'
      component='div'
      sx={{ fontSize: '12px', fontStyle: 'italic' }}
    >
      {knob.description}
    </Typography>
  </Box>
);

const groupOrder = [...new Set(KNOBS.map(knob => knob.group))];

export const FuzzySearchSettingsPanelInner = () => {
  const { settings, setOption, reset } = useFuzzySearchSettings();

  return (
    <Popover
      buttonContent={
        <>
          <DisplaySettingsIcon sx={{ mr: 1 }} />
          Search settings
        </>
      }
      buttonStyles={{
        width: 171,
        height: 32,
        px: 1,
        justifyContent: 'flex-start',
        whiteSpace: 'nowrap'
      }}
      buttonVariant='outlined'
      placement='bottom-end'
      zIndex={Z_INDEX.priorityMid}
    >
      <Box
        sx={{
          minWidth: 300,
          maxHeight: 480,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          fontSize: '14px'
        }}
      >
        {groupOrder.map((group, index) => (
          <Accordion
            key={group}
            defaultExpanded={index === 0}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ '&.Mui-expanded': { backgroundColor: 'gx.tileLight' } }}
            >
              {group}
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: 'gx.tileLight' }}>
              {KNOBS.filter(knob => knob.group === group).map(knob => (
                <Fragment key={knob.key}>{renderKnob(knob, settings, setOption)}</Fragment>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <ButtonBase
            sx={{ color: 'gx.highlight', px: 0.2 }}
            onClick={reset}
          >
            Reset to defaults
          </ButtonBase>
        </Box>
      </Box>
    </Popover>
  );
};

// TODO(fuzzy-search-tuning): delete this whole folder once tuning params are finalized
export default withFeatureFlag(
  FeatureFlag.CatalogFuzzySearchSettings,
  FuzzySearchSettingsPanelInner
);
